/**
 * POSShiftManager - Handles shift opening/closing from POS
 * Shows shift status and allows staff to open/close shifts directly from POS
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Clock, DollarSign, AlertTriangle, Check, X, LogOut,
  ChevronDown, ChevronUp, Banknote
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { posApi } from '@/utils/apiClient';

// Fiji Dollar denominations for cash counting
const DENOMINATIONS = [
  { value: 100, label: '$100', type: 'note' },
  { value: 50, label: '$50', type: 'note' },
  { value: 20, label: '$20', type: 'note' },
  { value: 10, label: '$10', type: 'note' },
  { value: 5, label: '$5', type: 'note' },
  { value: 2, label: '$2', type: 'note' },
  { value: 1, label: '$1', type: 'note' },
  { value: 0.5, label: '50c', type: 'coin' },
  { value: 0.2, label: '20c', type: 'coin' },
  { value: 0.1, label: '10c', type: 'coin' },
  { value: 0.05, label: '5c', type: 'coin' },
];

// Hook to manage shift state
export function useShiftManager(staffId) {
  const [activeShift, setActiveShift] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkActiveShift = useCallback(async () => {
    try {
      const res = await posApi.get('/pos/shifts/active');
      setActiveShift(res.data);
    } catch (err) {
      console.error('Failed to check shift:', err);
      setActiveShift(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkActiveShift();
  }, [checkActiveShift]);

  return { activeShift, loading, refreshShift: checkActiveShift };
}

// Component to prompt opening a shift
export function POSOpenShiftPrompt({ staff, onShiftOpened, onSkip }) {
  const [cashCount, setCashCount] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCashCount, setShowCashCount] = useState(false);

  const calculateTotal = () => {
    return DENOMINATIONS.reduce((sum, denom) => {
      const count = parseInt(cashCount[denom.value] || 0);
      return sum + (count * denom.value);
    }, 0);
  };

  const handleOpenShift = async () => {
    const total = calculateTotal();
    if (total <= 0) {
      toast.error('Please count the opening cash');
      return;
    }

    setLoading(true);
    try {
      const res = await posApi.post('/pos/shifts', {
        cashier_name: staff?.name || 'POS Staff',
        cashier_pin: staff?.pin || '000000',
        opening_cash: total,
        cash_count: cashCount,
        notes: notes || 'Shift opened from POS'
      });
      
      toast.success('Shift opened successfully!');
      onShiftOpened(res.data.shift);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to open shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <Clock className="w-8 h-8 text-amber-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">No Active Shift</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            You need to open a shift before taking orders
          </p>
        </div>

        {/* Quick Open with $400 */}
        <div className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-xl p-4 mb-4">
          <p className="text-sm text-teal-700 dark:text-teal-300 mb-3">
            Standard opening float: <strong>$400.00 FJD</strong>
          </p>
          <Button
            onClick={() => {
              setCashCount({ 100: 4 }); // 4 x $100 = $400
              setShowCashCount(true);
            }}
            className="w-full bg-teal-600 hover:bg-teal-700"
          >
            <Banknote className="w-4 h-4 mr-2" />
            Open with $400 Float
          </Button>
        </div>

        {/* Cash Count Section */}
        {showCashCount && (
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4 mb-4">
            <h3 className="font-medium text-gray-900 dark:text-white mb-3">Count Opening Cash</h3>
            <div className="grid grid-cols-4 gap-2 mb-4">
              {DENOMINATIONS.map(denom => (
                <div key={denom.value} className="text-center">
                  <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                    {denom.label}
                  </label>
                  <Input
                    type="number"
                    min="0"
                    value={cashCount[denom.value] || ''}
                    onChange={(e) => setCashCount({
                      ...cashCount,
                      [denom.value]: e.target.value
                    })}
                    className="text-center h-10"
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-between items-center py-3 border-t border-gray-200 dark:border-gray-700">
              <span className="font-medium text-gray-700 dark:text-gray-300">Total:</span>
              <span className="text-2xl font-bold text-teal-600">${calculateTotal().toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          {onSkip && (
            <Button
              variant="outline"
              onClick={onSkip}
              className="flex-1"
            >
              Skip for Now
            </Button>
          )}
          <Button
            onClick={handleOpenShift}
            disabled={loading || calculateTotal() <= 0}
            className="flex-1 bg-teal-600 hover:bg-teal-700"
          >
            {loading ? 'Opening...' : 'Open Shift'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// Component to close shift from POS
export function POSCloseShiftDialog({ shift, onClose, onShiftClosed }) {
  const [cashCount, setCashCount] = useState({});
  const [varianceNote, setVarianceNote] = useState('');
  const [supervisorPin, setSupervisorPin] = useState('');
  const [loading, setLoading] = useState(false);

  const calculateTotal = () => {
    return DENOMINATIONS.reduce((sum, denom) => {
      const count = parseInt(cashCount[denom.value] || 0);
      return sum + (count * denom.value);
    }, 0);
  };

  const expectedCash = (shift?.opening_cash || 0) + (shift?.cash_sales || 0);
  const countedCash = calculateTotal();
  const variance = countedCash - expectedCash;

  const handleCloseShift = async () => {
    if (countedCash <= 0) {
      toast.error('Please count the closing cash');
      return;
    }

    if (Math.abs(variance) > 0.01 && !varianceNote) {
      toast.error('Please explain the variance');
      return;
    }

    if (!supervisorPin) {
      toast.error('Supervisor PIN required');
      return;
    }

    setLoading(true);
    try {
      await posApi.post(`/pos/shifts/${shift.id}/close`, {
        closing_cash: countedCash,
        cash_count: cashCount,
        variance_explanation: varianceNote,
        supervisor_pin: supervisorPin,
        notes: 'Shift closed from POS'
      });
      
      toast.success('Shift closed successfully!');
      onShiftClosed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to close shift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-amber-600" />
            Close Shift
          </DialogTitle>
          <DialogDescription>
            Count the cash drawer and close your shift
          </DialogDescription>
        </DialogHeader>

        {/* Summary */}
        <div className="grid grid-cols-3 gap-4 py-4">
          <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Expected</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">${expectedCash.toFixed(2)}</p>
          </div>
          <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
            <p className="text-xs text-gray-500 dark:text-gray-400">Your Count</p>
            <p className="text-xl font-bold text-gray-900 dark:text-white">${countedCash.toFixed(2)}</p>
          </div>
          <div className={`text-center p-3 rounded-lg ${
            Math.abs(variance) < 0.01 
              ? 'bg-green-100 dark:bg-green-900/30' 
              : 'bg-red-100 dark:bg-red-900/30'
          }`}>
            <p className="text-xs text-gray-500 dark:text-gray-400">Variance</p>
            <p className={`text-xl font-bold ${
              Math.abs(variance) < 0.01 
                ? 'text-green-600' 
                : 'text-red-600'
            }`}>
              {variance >= 0 ? '+' : ''}${variance.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Cash Count */}
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <h3 className="font-medium text-gray-900 dark:text-white mb-3">Count Closing Cash</h3>
          <div className="grid grid-cols-4 gap-2 mb-2">
            {DENOMINATIONS.map(denom => (
              <div key={denom.value} className="text-center">
                <label className="text-xs text-gray-500 dark:text-gray-400 block mb-1">
                  {denom.label}
                </label>
                <Input
                  type="number"
                  min="0"
                  value={cashCount[denom.value] || ''}
                  onChange={(e) => setCashCount({
                    ...cashCount,
                    [denom.value]: e.target.value
                  })}
                  className="text-center h-9 text-sm"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Variance Explanation */}
        {Math.abs(variance) > 0.01 && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
            <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 mb-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="font-medium">Variance Detected</span>
            </div>
            <Input
              placeholder="Explain the variance..."
              value={varianceNote}
              onChange={(e) => setVarianceNote(e.target.value)}
              className="mt-2"
            />
          </div>
        )}

        {/* Supervisor PIN */}
        <div>
          <label className="text-sm text-gray-600 dark:text-gray-400 block mb-2">
            Supervisor PIN (to approve closure)
          </label>
          <Input
            type="password"
            placeholder="Enter supervisor PIN"
            value={supervisorPin}
            onChange={(e) => setSupervisorPin(e.target.value)}
            maxLength={6}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            onClick={handleCloseShift}
            disabled={loading || countedCash <= 0 || !supervisorPin}
            className="flex-1 bg-amber-600 hover:bg-amber-700"
          >
            {loading ? 'Closing...' : 'Close Shift'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Shift status badge for POS header
export function POSShiftBadge({ shift, onEndShift }) {
  const [expanded, setExpanded] = useState(false);

  if (!shift) return null;

  const cashSales = shift.cash_sales || 0;
  const expectedCash = (shift.opening_cash || 0) + cashSales;

  return (
    <div className="relative">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-2 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
      >
        <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
        <span className="text-sm font-medium">Shift Active</span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {expanded && (
        <div className="absolute top-full right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Opening Float:</span>
              <span className="font-medium text-gray-900 dark:text-white">${(shift.opening_cash || 0).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Cash Sales:</span>
              <span className="font-medium text-green-600">${cashSales.toFixed(2)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 dark:border-gray-700 pt-2">
              <span className="text-gray-500 dark:text-gray-400">Expected Cash:</span>
              <span className="font-bold text-gray-900 dark:text-white">${expectedCash.toFixed(2)}</span>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onEndShift}
            className="w-full mt-4 text-amber-600 border-amber-300 hover:bg-amber-50"
          >
            <LogOut className="w-4 h-4 mr-2" />
            End Shift
          </Button>
        </div>
      )}
    </div>
  );
}

export default POSOpenShiftPrompt;
